/**
 * The merge markers a campaign letter may carry, named once for both ends.
 *
 * The editor lists them for the operator (`web_crm` MessagePanel) and the
 * sender fills them in (`@wa/api` crm-marketing). Two agreeing lists in two
 * applications is how the editor offers `{{companyName}}` while the sender
 * replaces `{{company}}`, and that failure is silent in the worst way: the
 * letter sends, looks finished, and carries a literal `{{companyName}}` in the
 * first line. Same reasoning as `unsubscribe.ts` beside it.
 *
 * Pure module. No clock, no DOM, no imports.
 */

/**
 * Everything the editor offers.
 *
 * `unsubscribe` is in this list because the operator writes it, and NOT in
 * `MERGE_TOKENS` because the sender does not fill it here: it is per recipient
 * and legally load-bearing, so it is resolved by the decorator, which refuses
 * to build a letter at all when it has no working address to put there.
 */
export const LETTER_TOKENS = ["company", "regCode", "domain", "unsubscribe"] as const;
export type LetterToken = (typeof LETTER_TOKENS)[number];

/** The ones filled from the ledger row, in the order the editor shows them. */
export const MERGE_TOKENS = ["company", "regCode", "domain"] as const;
export type MergeToken = (typeof MERGE_TOKENS)[number];

export type MergeValues = Partial<Record<MergeToken, string | null | undefined>>;

/** The marker for one token, exactly as it appears in a body. */
export function letterToken(name: LetterToken): string {
  return `{{${name}}}`;
}

/**
 * Fill the markers this row can fill.
 *
 * EVERY occurrence, with `split`/`join` rather than a regular expression: the
 * values are data - a company name legitimately contains `$&`, brackets and
 * quotes - and a replacement string is where those turn into something else.
 *
 * A marker with no value becomes an empty string rather than staying visible.
 * A reader shown `{{company}}` learns the letter was sent by a machine that
 * did not check; a reader shown a slightly awkward sentence learns nothing at
 * all. The caller decides what "no value" means and may pass a fallback: the
 * sender walks company name, then domain, then registry code.
 */
export function applyMergeTokens(body: string, values: MergeValues): string {
  let out = body;
  for (const name of MERGE_TOKENS) {
    const marker = letterToken(name);
    if (!out.includes(marker)) continue;
    out = out.split(marker).join((values[name] ?? "").trim());
  }
  return out;
}

/** Which markers this body still carries. For a preview, never for a gate. */
export function unfilledTokens(body: string): LetterToken[] {
  return LETTER_TOKENS.filter((name) => body.includes(letterToken(name)));
}
